import { prepareBattleSoundtrack } from './presentation/audio/battle-soundtrack';
import { showDiagnosticsWarning } from './presentation/diagnostics-export';
import { createDiagnosticsArchive, createIndexedDbDiagnosticsStore } from './diagnostics/archive';
import { createMainMenu } from './presentation/menu/main-menu';
import type { AppShell } from './presentation/app-shell';
import type { DifficultyPreset } from './simulation/ai/difficulty';

function bootstrap(): void {
  const mount = document.getElementById('app');
  const menuMount = document.getElementById('menu');
  if (!mount || !menuMount) throw new Error('mount #app or #menu not found');

  const diagnostics = createDiagnosticsArchive(createIndexedDbDiagnosticsStore(), () => {
    showDiagnosticsWarning(
      document,
      'Не удалось сохранить логи в браузере. Выгрузите их в файл до закрытия страницы. Ранее сохранённые бои могут быть недоступны.',
    );
  });
  let runtime: AppShell | undefined;
  let starting = false;
  let failed = false;
  const menu = createMainMenu({
    mount: menuMount,
    requestDiagnosticsExport: () => diagnostics.exportJsonl(),
    onStart: (options) => {
      void start(options);
    },
  });
  document.getElementById('boot-heading')?.remove();
  prepareBattleSoundtrack(document);

  async function start(options: { difficulty: DifficultyPreset }): Promise<void> {
    if (starting) return;
    if (failed) {
      window.location.reload();
      return;
    }
    if (runtime) {
      menu.setLoading(true);
      runtime.startMatch(options);
      return;
    }
    starting = true;
    menu.setLoading(true);

    // Resume inside the click gesture, before loading the runtime loses transient
    // activation. Match audio takes ownership of this same context once ready.
    let context: AudioContext | undefined;
    try {
      if (typeof AudioContext !== 'undefined') {
        context = new AudioContext();
        void context.resume().catch((error: unknown) => console.warn('Audio unavailable', error));
      }
    } catch (error) {
      console.warn('Audio unavailable', error);
    }

    const canvas = document.createElement('canvas');
    canvas.dataset.testid = 'game-canvas';
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'Fall Protocol — поле боя');
    Object.assign(canvas.style, { width: '100%', height: '100%', display: 'block' });
    mount!.appendChild(canvas);
    try {
      const { configureAppShell } = await import('./presentation/app-shell');
      const readyContext = context;
      runtime = await configureAppShell({
        canvas,
        mount: mount!,
        menu,
        diagnostics,
        ...(readyContext ? { audioContextFactory: () => readyContext } : {}),
      });
      runtime.startMatch(options);
    } catch (error) {
      console.error('Battlefield could not start', error);
      if (context) void context.close().catch(() => {});
      // A partially initialized renderer cannot safely be retried in place.
      // Reload also clears any resources allocated before the failed step.
      failed = true;
      menu.showLoadError();
    } finally {
      starting = false;
    }
  }
}

if (import.meta.env.DEV && new URLSearchParams(window.location.search).has('playtest')) {
  // The observer intentionally remains plain JS: it is DEV instrumentation, not application code.
  // @ts-expect-error -- Vite bundles this DEV-only JS module; TypeScript does not type-check it.
  void import('./dev/playtest/playtest-observer.js').catch((error) => {
    console.warn('[playtest-observer] DEV import failed', error);
  });
}

bootstrap();
